<template>
  <div id="app">
    <div class="top-bar">
      <h2 class="title">{{ title }}</h2>
    </div>

    <div class="update-bar">
      <span class="update-time">更新时间:{{ updateTime }}</span>
      <div style="display: flex; align-items: center; gap: 10px;">
        <!-- 授权状态显示 -->
        <el-tag v-if="isWechatEnv" :type="openId ? 'success' : 'warning'" size="small">
          {{ openId ? '已授权' : '未授权' }}
        </el-tag>
        <el-button type="text" size="small" @click="handleRefresh">
          <i class="el-icon-refresh"></i> 刷新
        </el-button>
        <el-button v-if="isWechatEnv && !openId" type="text" size="small" @click="handleWechatAuth">
          <i class="el-icon-user"></i> 微信授权
        </el-button>
        <el-button v-if="openId" type="text" size="small" @click="showAuthInfo">
          <i class="el-icon-info"></i> 授权信息
        </el-button>
        <el-button v-if="openId" type="text" size="small" @click="handleClearAuth" style="color: #f56c6c;">
          <i class="el-icon-delete"></i> 清除授权
        </el-button>
      </div>
    </div>
    <el-alert
              title="温馨提示"
              type="warning"
              :closable="false"
              style="margin-top: 20px"
            >
              <div class="warning-list">
                <p style="color: rgb(255, 0, 0);">1. 数据仅供参考，不构成投资建议！</p>
                <p style="color: rgb(255, 0, 0);" >2. 数据有一定的延时性，请关注更新时间！</p>
                <p style="color: rgb(255, 0, 0);" >3. 估值可能会有一定的误差，请谨慎参考！</p>
                <p style="color: rgb(255, 0, 0);">4. 当二级市场价格高于基金净值时，出现套利机会</p>
                <p style="color: rgb(255, 0, 0);">5. 16开头的时深市，可以一拖六，海外（例如标普）申购周期T+3,国内（白银）T+2</p>
                <p style="color: rgb(255, 0, 0);">6. 50开头的时沪市，不能一拖六，场内购买需要申购1000，超出部分退回，或者场外申购后在转场内</p>	
                <p style="color: rgb(255, 0, 0);">7. 可扫码关注微信小程序端或者微信公众号查看相关信息，三端数据保持一致</p>	
                <p style="color: rgb(255, 0, 0);">8. 扫描右侧群聊二维码可加入套利交流群</p>				
              </div>
    </el-alert>
    <el-tabs v-model="currentTab" @tab-click="handleTabClick">
      <el-tab-pane label="LOF数据" name="lof">
        <FundTable
          :rows="rows"
          :columns="columns"
          :loading="loading"
        />
      </el-tab-pane>

      <el-tab-pane label="ETF数据" name="etf">
        <FundTable
          :rows="rows"
          :columns="columns"
          :loading="loading"
        />
      </el-tab-pane>

      <el-tab-pane label="相关信息" name="info">
        <InfoPage />
      </el-tab-pane>

      <el-tab-pane label="套利拖拉机注册步骤" name="register">
        <RegisterPage />
      </el-tab-pane>
    </el-tabs>
    
    <!-- 悬浮二维码组件 -->
    <FloatQRCode />
  </div>
</template>

<script>
import axios from 'axios'
import { fetchFundData, formatUpdateTime } from '@/api/fundApi'
import FundTable from '@/components/FundTable.vue'
import InfoPage from '@/components/InfoPage.vue'
import RegisterPage from '@/components/RegisterPage.vue'
import FloatQRCode from '@/components/FloatQRCode.vue'
import { initWechatAuth, getStoredOpenId, isWechatBrowser, getWechatAuthUrl, clearStoredOpenId } from '@/utils/wechatAuth'

export default {
  name: 'App',
  components: {
    FundTable,
    InfoPage,
    RegisterPage,
    FloatQRCode
  },
  data() {
    return {
      title: 'LOF基金溢价率实时数据',
      currentTab: 'lof',
      updateTime: '--',
      columns: [],
      rows: [],
      loading: false,
      backendUrl: 'https://qcwechat.cn',
      openId: null,
      isWechatEnv: false
    }
  },
  mounted() {
    console.log('=== App mounted ===')
    console.log('当前URL:', window.location.href)

    // 检查是否在微信环境
    this.isWechatEnv = isWechatBrowser()
    console.log('isWechatEnv:', this.isWechatEnv)

    // 检查URL中是否有openId参数（授权回调）
    const urlParams = new URLSearchParams(window.location.search)
    const openIdFromUrl = urlParams.get('openId')

    if (openIdFromUrl) {
      console.log('从URL获取到OpenId:', openIdFromUrl)
      this.openId = openIdFromUrl
      localStorage.setItem('wechat_openId', openIdFromUrl)

      const userId = urlParams.get('userId')
      if (userId) {
        localStorage.setItem('wechat_userId', userId)
      }

      // 清除URL参数
      const cleanUrl = window.location.origin + window.location.pathname
      window.history.replaceState({}, '', cleanUrl)

      this.$message.success('微信授权成功')
    } else {
      // 从本地存储获取
      const storedOpenId = getStoredOpenId()
      if (storedOpenId) {
        console.log('从localStorage获取到OpenId:', storedOpenId)
        this.openId = storedOpenId

        // 验证openId是否有效（异步验证，不阻塞页面）
        this.validateOpenId(storedOpenId)
      }
    }

    // 加载数据
    this.fetchList()
  },
  methods: {
    // 验证OpenId是否有效
    async validateOpenId(openId) {
      try {
        const response = await axios.get(`${this.backendUrl}/wechat/oauth/userInfo`, {
          params: { openId },
          timeout: 5000
        })

        if (response.data.code !== 200) {
          console.warn('⚠️ OpenId验证失败，可能已失效:', response.data.msg)
          // OpenId无效，清除本地存储
          clearStoredOpenId()
          this.openId = null
          this.$message.warning('授权信息已失效，请重新授权')
        } else {
          // 检查用户状态
          const userInfo = response.data.data
          if (userInfo.status === '1') {
            console.warn('⚠️ 用户已停用，需要重新授权')
            clearStoredOpenId()
            this.openId = null
            this.$message.warning('授权已停用，请重新授权')
          } else {
            console.log('✅ OpenId验证成功')
          }
        }
      } catch (error) {
        console.error('验证OpenId失败:', error)
        // 网络错误不清除，避免误删
      }
    },

    // 获取数据
    async fetchList() {
      this.loading = true
      try {
        const result = await fetchFundData(this.currentTab, this.backendUrl)
        this.columns = result.columns
        this.rows = result.rows
        this.updateTime = result.updateTime
      } catch (error) {
        this.$message.error('数据加载失败，请稍后重试')
        this.columns = []
        this.rows = []
        this.updateTime = formatUpdateTime()
      } finally {
        this.loading = false
      }
    },

    // Tab切换
    handleTabClick(tab) {
      this.currentTab = tab.name
      // 只有 LOF 和 ETF 标签页需要加载数据
      if (tab.name === 'lof') {
        this.title = 'LOF基金溢价率实时数据'
        this.fetchList()
      } else if (tab.name === 'etf') {
        this.title = 'ETF基金溢价率实时数据'
        this.fetchList()
      } else if (tab.name === 'info') {
        this.title = '相关信息'
      } else if (tab.name === 'register') {
        this.title = '套利拖拉机注册步骤'
      }
    },

    // 刷新数据
    handleRefresh() {
      this.fetchList()
      this.$message.success('刷新成功')
    },

    // 手动触发微信授权
    async handleWechatAuth() {
      if (!this.isWechatEnv) {
        this.$message.warning('请在微信中打开此页面')
        return
      }

      try {
        this.$message.info('正在跳转授权页面...')
        const frontendUrl = encodeURIComponent(window.location.href.split('?')[0])
        const callbackUrl = `https://qcwechat.cn/wechat/oauth/page-callback?frontendUrl=${frontendUrl}`
        const authUrl = await getWechatAuthUrl(callbackUrl)
        window.location.href = authUrl
      } catch (error) {
        console.error('获取授权链接失败:', error)
        this.$message.error('获取授权链接失败：' + error.message)
      }
    },

    // 显示授权信息
    async showAuthInfo() {
      try {
        // 从后台获取最新用户信息
        const response = await axios.get(`${this.backendUrl}/wechat/oauth/userInfo`, {
          params: { openId: this.openId },
          timeout: 5000
        })

        if (response.data.code === 200) {
          const userInfo = response.data.data
          const sexMap = { '0': '男', '1': '女', '2': '未知' }
          const sexText = sexMap[userInfo.sex] || '未知'
          const statusMap = { '0': '正常', '1': '已停用' }
          const statusText = statusMap[userInfo.status] || '未知'

          let infoHtml = `
            <div style="text-align: left;">
              <p><strong>OpenId：</strong>${userInfo.openId || '未获取'}</p>
              <p><strong>用户ID：</strong>${userInfo.userId || '未获取'}</p>
              <p><strong>昵称：</strong>${userInfo.nickName || '未获取'}</p>
              <p><strong>性别：</strong>${sexText}</p>
              <p><strong>地区：</strong>${userInfo.country || ''} ${userInfo.province || ''} ${userInfo.city || ''}</p>
              <p><strong>状态：</strong><span style="color: ${userInfo.status === '0' ? '#67C23A' : '#F56C6C'}">${statusText}</span></p>
              <p><strong>最后访问：</strong>${userInfo.lastAccessTime || '未记录'}</p>
              <p><strong>环境：</strong>${this.isWechatEnv ? '微信浏览器' : '普通浏览器'}</p>
            </div>
          `

          // 如果有头像，显示头像
          if (userInfo.avatar) {
            infoHtml = `
              <div style="text-align: center; margin-bottom: 15px;">
                <img src="${userInfo.avatar}" style="width: 80px; height: 80px; border-radius: 50%;" />
              </div>
            ` + infoHtml
          }

          this.$alert(infoHtml, '授权信息', {
            confirmButtonText: '确定',
            dangerouslyUseHTMLString: true
          })

          // 同时在控制台输出
          console.log('=== 微信授权信息 ===')
          console.log('OpenId:', userInfo.openId)
          console.log('UserId:', userInfo.userId)
          console.log('昵称:', userInfo.nickName)
          console.log('性别:', sexText)
          console.log('地区:', userInfo.country, userInfo.province, userInfo.city)
          console.log('头像:', userInfo.avatar)
          console.log('最后访问:', userInfo.lastAccessTime)
          console.log('是否微信环境:', this.isWechatEnv)
          console.log('==================')
        } else {
          this.$message.error('获取用户信息失败：' + response.data.msg)
        }
      } catch (error) {
        console.error('获取用户信息失败:', error)
        // 降级显示本地缓存信息
        const userId = localStorage.getItem('wechat_userId')
        const info = `授权状态：已授权\nOpenId：${this.openId}\n用户ID：${userId || '未获取'}\n环境：${this.isWechatEnv ? '微信浏览器' : '普通浏览器'}`
        this.$alert(info, '授权信息', {
          confirmButtonText: '确定'
        })
      }
    },

    // 清除授权
    handleClearAuth() {
      this.$confirm('确定要清除授权信息吗？清除后需要重新授权。', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          // 先调用后台接口停用用户
          const response = await axios.get(`${this.backendUrl}/wechat/oauth/disable`, {
            params: { openId: this.openId },
            timeout: 5000
          })

          if (response.data.code === 200) {
            console.log('✅ 后台已停用用户')
          } else {
            console.warn('⚠️ 后台停用失败:', response.data.msg)
            // 即使后台失败也继续清除前端
          }
        } catch (error) {
          console.error('❌ 调用停用接口失败:', error)
          // 网络错误也继续清除前端
        }

        // 清除前端localStorage
        clearStoredOpenId()
        this.openId = null
        this.$message.success('授权信息已清除')
        console.log('✅ 已清除本地授权信息')
      }).catch(() => {
        // 取消操作
      })
    }
  }
}
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

#app {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB',
    'Microsoft YaHei', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  background: #f8f9fb;
  padding: 12px;
  font-size: 13px;
  max-width: 1200px;
  margin: 0 auto;
  zoom: 0.85;
}

.top-bar {
  background: #fff;
  padding: 14px 16px;
  margin-bottom: 12px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.update-bar {
  background: #fff;
  padding: 10px 16px;
  margin-bottom: 12px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.update-time {
  color: #888;
  font-size: 13px;
}
</style>

<style>
/* Element UI 自定义样式 - 全局样式不使用 scoped */
.el-table th.el-table__cell {
  background: linear-gradient(90deg, #ff8a2d 0%, #ff6723 100%) !important;
  color: #fff !important;
  font-weight: bold !important;
}

.el-tabs__item.is-active {
  color: #ff6f2f;
}

.el-tabs__active-bar {
  background-color: #ff6f2f;
}

.el-alert__title {
  font-weight: 600;
}
</style>
